import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Compass, Home, Hash, Lock, Flame, Radio, Sparkles, Heart, X, ArrowRight, ArrowLeft, Check, Layers, Bookmark, Store, MessageSquareQuote, ShieldAlert, Crosshair, Bell, Share2 } from "lucide-react";
import { glowTarget } from "../lib/guideGlow";
import { useAuth } from "../context/AuthContext";

const TOUR_ID = "orientation";
const TOUR_REV = 2;
const SEEN_KEY = `${TOUR_ID}@${TOUR_REV}`;
// Members who finished an earlier version reopen straight on the newest step.
const PREV_SEEN_KEY = `${TOUR_ID}@${TOUR_REV - 1}`;
const NEW_STEP_TITLE = "Share the Signal";

// Render **bold** emphasis inside chapter body text.
const Rich = ({ text }) => (
  <>{String(text || "").split("**").map((seg, i) => (i % 2 ? <span key={i} className="text-white font-medium">{seg}</span> : <React.Fragment key={i}>{seg}</React.Fragment>))}</>
);

// Chapters are a function of the current user id so profile deep-links land on the member's own HQ.
const buildChapters = (uid) => [
  {
    icon: Compass, title: "Welcome, let's get you oriented",
    body: "This quick tour walks you through everything, step by step, so you feel completely at home here. It takes about a minute, and you can **reopen it anytime** with the **?** at the top, from your HQ or the Compass. Tap **Show me** on any step to jump straight to that feature, then **Back to the tour** to carry on.",
  },
  {
    icon: Home, title: "Your Profile is your HQ",
    body: "Your profile is home base. It holds your **identity** (name, headline and the realms you work in), your **Transmissions** (everything you post), your **invites**, and your **Resonance Points**. Two things worth knowing: you can **pin your best posts** to the top of your profile, and you can group posts into **Collections** (like series or playlists) so visitors can follow a whole body of work.",
    show: { path: `/profile/${uid}`, target: '[data-testid="profile-tab-collections"], [data-testid="profile-tabs"]' },
  },
  {
    icon: Bell, title: "Turn on notifications",
    body: "In your HQ, tap the **Notifications** button so you know the moment a sovereign friend **messages you**, **replies**, or **appreciates your work**, and when your communities post news or go live. You can switch them off anytime from the same button.",
    show: { path: `/profile/${uid}`, target: '[data-testid="notifications-toggle-button"], [data-testid="notifications-toggle"]' },
  },
  {
    icon: Layers, title: "Posting in realms",
    body: "Every post lives in a **realm**. Choose the one someone would **visit to find work like yours** (the essence of what you offer, not just its business category; up to 3). One important note: the **Signal Infrastructure** realm is only for Sovereign-Signal-related posts (feedback, ideas and announcements about the platform itself), not general content.",
    show: { path: "/feed", target: '[data-testid="compose-button"], [data-testid="compose-button-mobile"]' },
  },
  {
    icon: Hash, title: "Hashtags are our search engine",
    body: "There's no noisy algorithm here. Instead, **your hashtags power search**. Keep them practical and logical (what something actually *is*), and fellow sovereigns will find you and the topics they care about. Thoughtful tags are a gift to the whole community.",
    show: { path: "/feed", target: '[data-testid="signal-search-input"]' },
  },
  {
    icon: Lock, title: "Private by design",
    body: "Your posts and direct messages are **end-to-end encrypted**: scrambled on your own device before they ever leave it. Even the founder and the Council **cannot read them**. To connect one-to-one, open a member's profile and start an **Exchange** (a private, encrypted message).",
    show: { path: "/messages", target: '[data-testid="new-exchange-btn"]' },
  },
  {
    icon: Bookmark, title: "Saved: your private mini-database",
    body: "Anything you save is **end-to-end encrypted and visible only to you**. Even better, you can **leave yourself private notes** on saved posts, turning your Saved space into a quiet personal database for research, references, ideas and things to revisit. No one else can ever see it.",
    show: { path: "/saved", target: '[data-testid="saved-category-filters"], [data-testid="saved-note"], [data-testid="post-save-button"], [data-testid="saved-empty"], [data-testid="saved-page"]' },
  },
  {
    icon: Store, title: "The Business Directory",
    body: "Have a business or an offering? The **Business Directory** realm is where you **post your business and discover what other sovereigns are building**: a living, member-run directory. In the feed, filter to *Business Directory* to explore it, or add your own.",
    show: { path: "/feed", target: '[data-testid="realm-filter"]' },
  },
  {
    icon: Flame, title: "The Beacon: your creator space",
    body: "When you're ready to teach, sell, or share a body of work, light a **Beacon**: your own beautiful space for videos, audio, art, lessons and live sessions, offered to members freely or by subscription, with you keeping 99% of your price (SovSig takes just 1%). It's entirely optional, and entirely yours to shape.",
    show: { path: "/beacon" },
  },
  {
    icon: Radio, title: "The Conscious Stream",
    body: "The **Conscious Stream** is a curated current of hand-picked videos and writings from across the wider web, watched **privately** (we relay thumbnails through our own server, so Google never sees you here). Find something worth discussing? **Share it into a realm** to open an encrypted conversation around it.",
    show: { path: "/stream" },
  },
  {
    icon: MessageSquareQuote, title: "Sovereign Voices",
    body: "**Sovereign Voices** is our wall of testimonials: members sharing, in their own words, what this space means to them. **Read what others have said** to feel the heart of the community, and when you're moved, **add your own voice**. It helps new sovereigns know they've found the right place.",
    show: { path: "/voices" },
  },
  {
    icon: Sparkles, title: "Resonance Points",
    body: "Bring sovereigns you'd vouch for into the field and you earn **Resonance Points** as they take root. They're **private to you** (no leaderboards, no comparisons) and they'll unlock more as the Signal grows. Your personal invite key and your points both live on your profile.",
    show: { path: `/profile/${uid}?focus=resonance` },
  },
  {
    icon: Share2, title: "Share the Signal",
    body: "Know a kindred soul who belongs here? In your HQ, in your **Resonance Points** card, tap **Share the Signal cards**: 8 beautiful \"What is Sovereign Signal\" cards with **your invite key printed on every one**. Select one, a few or **all 8** and post them together, to Instagram, Stories or anywhere you like. Even better, **share the page link too**: anyone who opens it has **your key filled in automatically** when they request an invite, so every sovereign who joins through you is counted as yours.",
    show: { path: `/profile/${uid}?focus=resonance`, target: '[data-testid="resonance-share-cards"]' },
  },
  {
    icon: ShieldAlert, title: "Contact the Council: we're listening",
    body: "Have an **idea, suggestion, question or comment**? Tap **Contact the Council** in the menu (on phones, it's under **More**). Your message goes straight to the stewards, and replies come back to you here. Every bit of feedback helps shape the Signal.",
    show: { target: '[data-testid="nav-council"], [data-testid="nav-more-bottom"]' },
  },
  {
    icon: Heart, title: "One rule, and where to go deeper",
    body: "Our single rule: **we are all sovereigns here. Treat one another with respect.** That's the whole culture. For the deeper *why* behind everything (privacy, realms, invite keys and more), the **Compass** is always a tap away.",
    show: { path: "/compass" },
  },
];

const OrientationContext = createContext({ openTour: () => {} });
export const useOrientation = () => useContext(OrientationContext);

export const OrientationProvider = ({ children }) => {
  const { user, dismissTip } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false); // after "Show me": a pill lets members resume where they left off
  const autoTried = useRef(false);
  const location = useLocation();

  const uid = user?.id;
  const chapters = buildChapters(uid);
  const seen = (user?.dismissed_tips || []).includes(SEEN_KEY);

  const markDone = useCallback(() => {
    if (!seen) dismissTip(TOUR_ID, TOUR_REV);
  }, [seen, dismissTip]);

  // Auto-open once, for members who haven't seen it yet.
  // Note: set autoTried INSIDE the timeout so React 18 StrictMode's mount→cleanup→
  // remount (which clearTimeouts the first timer) still lets the second mount reschedule.
  useEffect(() => {
    if (!user || autoTried.current || seen) return;
    // Wait while the security (2-step) reminder is showing so the two pop-ups never stack.
    let t;
    const attempt = () => {
      if (window.__ssMfaNudgeActive || document.querySelector('[data-testid="mfa-nudge"]')) { t = setTimeout(attempt, 1200); return; }
      autoTried.current = true;
      const returning = (user.dismissed_tips || []).includes(PREV_SEEN_KEY);
      setStep(returning ? Math.max(0, chapters.findIndex((c) => c.title === NEW_STEP_TITLE)) : 0);
      setOpen(true);
    };
    t = setTimeout(attempt, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, seen]);

  const openTour = useCallback(() => { setPaused(false); setStep(0); setOpen(true); }, []);
  // "?" in the top bar: resume a paused tour, otherwise start fresh.
  const helpTour = useCallback(() => { if (!paused) setStep(0); setPaused(false); setOpen(true); }, [paused]);
  const close = () => { markDone(); setOpen(false); setPaused(false); window.__ssTourPaused = false; };
  const showMe = ({ path, target }) => {
    setOpen(false); setPaused(true); window.__ssTourPaused = true;
    const here = path && `${location.pathname}${location.search}` === path;
    if (path && !here) navigate(path);
    if (target) glowTarget(target, { missing: null });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const resume = () => { setPaused(false); setOpen(true); window.__ssTourPaused = false; };
  const next = () => { if (step >= chapters.length - 1) { close(); } else { setStep((s) => s + 1); } };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const ch = chapters[step];
  const Icon = ch?.icon || Compass;
  const isLast = step === chapters.length - 1;

  return (
    <OrientationContext.Provider value={{ openTour, helpTour }}>
      {children}
      {paused && !open && (
        <div className="fixed inset-x-0 bottom-24 md:bottom-8 z-[55] flex justify-center pointer-events-none px-4">
        <div className="pointer-events-auto whitespace-nowrap flex items-center gap-1 bg-[#121212] border border-[#14A89A]/50 shadow-2xl shadow-black/60 rounded-full pl-4 pr-1.5 py-1.5 fade-up" data-testid="orientation-resume-pill">
          <button onClick={resume} data-testid="orientation-resume" className="font-body text-sm text-white flex items-center gap-2"><ArrowLeft size={14} className="text-[#14A89A]" /> Back to the tour <span className="text-zinc-500 text-xs">· {step + 1}/{chapters.length}</span></button>
          <button onClick={close} aria-label="End tour" data-testid="orientation-resume-close" className="ml-1 w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-500 hover:text-white"><X size={14} /></button>
        </div>
        </div>
      )}
      {open && ch && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="orientation-tour">
          <div className="relative w-full max-w-lg bg-[#121212] border border-white/10 shadow-2xl shadow-black/60 fade-up">
            {/* progress bar */}
            <div className="h-1 w-full bg-white/5">
              <div className="h-full bg-[#14A89A] transition-all duration-300" style={{ width: `${((step + 1) / chapters.length) * 100}%` }} />
            </div>

            <button onClick={close} aria-label="Skip tour" data-testid="orientation-skip" className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
              <X size={16} />
            </button>

            <div className="p-7">
              <div className="flex items-center gap-2 mb-4">
                <span className="font-body text-[10px] tracking-[0.25em] uppercase text-[#14A89A]">Orientation</span>
                <span className="font-body text-[10px] tracking-[0.2em] uppercase text-zinc-600">· Step {step + 1} of {chapters.length}</span>
              </div>

              <div className="flex items-start gap-3.5 mb-3">
                <div className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center bg-[#14A89A]/12 text-[#14A89A]">
                  <Icon size={22} strokeWidth={1.75} />
                </div>
                <h2 className="font-heading text-xl text-white leading-snug pt-1.5" data-testid="orientation-title">{ch.title}</h2>
              </div>

              <p className="font-body text-sm text-zinc-300 leading-relaxed"><Rich text={ch.body} /></p>

              {ch.show && (
                <button onClick={() => showMe(ch.show)} data-testid="orientation-showme" className="mt-4 inline-flex items-center gap-1.5 border border-[#14A89A]/50 text-[#14A89A] px-3 py-1.5 font-body text-xs hover:bg-[#14A89A]/10 transition-colors">
                  <Crosshair size={13} /> Show me
                </button>
              )}

              {/* controls */}
              <div className="flex items-center justify-between mt-7 pt-5 border-t border-white/10">
                <button onClick={back} disabled={step === 0} data-testid="orientation-back" className="inline-flex items-center gap-1.5 font-body text-sm text-zinc-400 hover:text-white disabled:opacity-30 disabled:hover:text-zinc-400 transition-colors">
                  <ArrowLeft size={15} /> Back
                </button>

                <div className="flex items-center gap-1.5">
                  {chapters.map((_, i) => (
                    <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-4 bg-[#14A89A]" : "w-1.5 bg-white/15"}`} />
                  ))}
                </div>

                {isLast ? (
                  <button onClick={close} data-testid="orientation-finish" className="inline-flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] font-semibold font-body text-sm px-4 py-2 hover:brightness-110 transition">
                    <Check size={15} /> Finish
                  </button>
                ) : (
                  <button onClick={next} data-testid="orientation-next" className="inline-flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] font-semibold font-body text-sm px-4 py-2 hover:brightness-110 transition">
                    Next <ArrowRight size={15} />
                  </button>
                )}
              </div>

              {step === 0 && (
                <button onClick={close} data-testid="orientation-skip-text" className="mt-4 w-full text-center font-body text-xs text-zinc-600 hover:text-zinc-400 transition-colors">Skip for now</button>
              )}
            </div>
          </div>
        </div>
      )}
    </OrientationContext.Provider>
  );
};
